import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2, Save, GripVertical } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

const formSchema = z.object({
  filters: z.object({
    showFilters: z.boolean().default(true),
    showSearch: z.boolean().default(true),
    showPriceRange: z.boolean().default(true),
    showStatus: z.boolean().default(true),
    showCategory: z.boolean().default(true),
    showCity: z.boolean().default(true),
    showBedrooms: z.boolean().default(true),
    showBathrooms: z.boolean().default(true),
    showSuites: z.boolean().default(true),
    showParkingSpaces: z.boolean().default(true),
    showArea: z.boolean().default(true),
  }),
  categoryOrganization: z.object({
    enabled: z.boolean().default(false),
    categories: z.array(z.object({
      name: z.string(),
      maxItems: z.number().min(1).max(50).default(12),
      enabled: z.boolean().default(true),
      order: z.number().default(0),
    })).default([]),
  }),
});

interface CategoryConfig {
  name: string;
  maxItems: number;
  enabled: boolean;
  order: number;
}

const defaultCategories: CategoryConfig[] = [
  { name: 'Lançamento', maxItems: 12, enabled: true, order: 0 },
  { name: 'Na planta', maxItems: 12, enabled: true, order: 1 },
  { name: 'Em Construção', maxItems: 12, enabled: true, order: 2 },
  { name: 'Pronto para morar', maxItems: 12, enabled: true, order: 3 },
];

export default function StorefrontFiltersManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const { user } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      filters: {
        showFilters: true,
        showSearch: true,
        showPriceRange: true,
        showStatus: true,
        showCategory: true,
        showCity: true,
        showBedrooms: true,
        showBathrooms: true,
        showSuites: true,
        showParkingSpaces: true,
        showArea: true,
      },
      categoryOrganization: {
        enabled: false,
        categories: defaultCategories,
      },
    },
  });

  useEffect(() => {
    if (user?.id) {
      loadSettings();
    }
  }, [user?.id]);

  const loadSettings = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('user_storefront_settings')
        .select('*')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        // Handle both old flat structure and new nested structure
        const filters = data.settings.filters || {
          showFilters: data.settings.showFilters ?? true,
          showSearch: data.settings.showSearch ?? true,
          showPriceRange: data.settings.showPriceRange ?? true,
          showStatus: data.settings.showStatus ?? true,
          showCategory: data.settings.showCategory ?? true,
          showCity: data.settings.showCity ?? true,
          showBedrooms: data.settings.showBedrooms ?? true,
          showBathrooms: data.settings.showBathrooms ?? true,
          showSuites: data.settings.showSuites ?? true,
          showParkingSpaces: data.settings.showParkingSpaces ?? true,
          showArea: data.settings.showArea ?? true,
        };

        const categoryOrganization = data.settings.categoryOrganization || {
          enabled: false,
          categories: defaultCategories,
        };

        form.reset({
          filters,
          categoryOrganization,
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setSaving(true);

      // Ensure we always send a properly structured settings object
      const settingsData = {
        filters: values.filters || {
          showFilters: true,
          showSearch: true,
          showPriceRange: true,
          showStatus: true,
          showCategory: true,
          showCity: true,
          showBedrooms: true,
          showBathrooms: true,
          showSuites: true,
          showParkingSpaces: true,
          showArea: true,
        },
        categoryOrganization: values.categoryOrganization || {
          enabled: false,
          categories: defaultCategories,
        },
      };

      const { data: existingSettings } = await supabase
        .from('user_storefront_settings')
        .select('id')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (existingSettings) {
        const { error } = await supabase
          .from('user_storefront_settings')
          .update({
            settings: settingsData,
          })
          .eq('id', existingSettings.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_storefront_settings')
          .insert({
            user_id: user?.id,
            settings: settingsData,
          });

        if (error) throw error;
      }

      toast.success('Configurações salvas com sucesso');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const currentCategories = form.getValues('categoryOrganization.categories');
    const newCategories = [...currentCategories];
    const draggedItem = newCategories[draggedIndex];
    
    // Remove the dragged item
    newCategories.splice(draggedIndex, 1);
    // Insert it at the new position
    newCategories.splice(dropIndex, 0, draggedItem);
    
    // Update order values
    const updatedCategories = newCategories.map((cat, index) => ({
      ...cat,
      order: index,
    }));

    form.setValue('categoryOrganization.categories', updatedCategories);
    setDraggedIndex(null);
  };

  const updateCategoryMaxItems = (index: number, maxItems: number) => {
    const currentCategories = form.getValues('categoryOrganization.categories');
    const updatedCategories = [...currentCategories];
    updatedCategories[index] = { ...updatedCategories[index], maxItems };
    form.setValue('categoryOrganization.categories', updatedCategories);
  };

  const updateCategoryEnabled = (index: number, enabled: boolean) => {
    const currentCategories = form.getValues('categoryOrganization.categories');
    const updatedCategories = [...currentCategories];
    updatedCategories[index] = { ...updatedCategories[index], enabled };
    form.setValue('categoryOrganization.categories', updatedCategories);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const categories = form.watch('categoryOrganization.categories');
  const categoryOrganizationEnabled = form.watch('categoryOrganization.enabled');

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-6">
          {/* Controle Geral de Filtros */}
          <FormField
            control={form.control}
            name="filters.showFilters"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    Mostrar Filtros
                  </FormLabel>
                  <FormDescription>
                    Exibir barra de filtros na vitrine (controla todos os filtros abaixo)
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Filtros Específicos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="filters.showSearch"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Busca por Texto
                    </FormLabel>
                    <FormDescription>
                      Campo de busca livre por título, cidade e endereço
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="filters.showStatus"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Filtro de Tipo
                    </FormLabel>
                    <FormDescription>
                      Filtrar por venda, aluguel ou ambos
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="filters.showCategory"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Filtro de Categoria
                    </FormLabel>
                    <FormDescription>
                      Filtrar por lançamento, na planta, etc.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="filters.showCity"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Filtro de Cidade
                    </FormLabel>
                    <FormDescription>
                      Filtrar imóveis por cidade
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="filters.showPriceRange"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Filtro de Preço
                    </FormLabel>
                    <FormDescription>
                      Slider para filtrar por faixa de preço
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="filters.showArea"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Filtro de Área
                    </FormLabel>
                    <FormDescription>
                      Slider para filtrar por área em m²
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="filters.showBedrooms"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Filtro de Quartos
                    </FormLabel>
                    <FormDescription>
                      Filtrar por número de quartos
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="filters.showBathrooms"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Filtro de Banheiros
                    </FormLabel>
                    <FormDescription>
                      Filtrar por número de banheiros
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="filters.showSuites"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Filtro de Suítes
                    </FormLabel>
                    <FormDescription>
                      Filtrar por número de suítes
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="filters.showParkingSpaces"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Filtro de Vagas
                    </FormLabel>
                    <FormDescription>
                      Filtrar por número de vagas de garagem
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="h-4 w-4 mr-2" />
            Salvar Configurações
          </Button>
        </div>
      </form>
    </Form>
  );
}